/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import ClockLoader from '@base/ClockLoader/ClockLoader';
import useInterval from '@hooks/useInterval';
import { useDispatchToStore } from '@hooks/reduxHooks';
import cookies from '@utils/cookies';
import pushRoute from '@utils/pushRoute';
import getQueryString from '@utils/checkout';
import { getJWTToken, getMe } from '@api/selectors/auth';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { abVariations } from '@utils/abHelper';
import {
  purchaseSubscription,
  checkForPaymentStatusJuspay,
  savePurchasedSubscription,
  fetchPartPaymentStatus
} from '@actions/checkout';
import { setPrePaymentSubscriptionUids } from '@actions/auth';
import {
  sendPaymentSuccessfulEvent,
  sendPaymentFailureEvent,
  sendPaymentPendingEvent
} from '@events/sendAnalytics';
import { Spacings, breakpoints, mediaQuery } from '@styles/index';
import UAIconsAPI from '@apicontrollers/UAIconsAPI/UAIconsAPI';
import { ICONS_GOAL_UID } from '@constants/unacademyIcons/icons.constants';
import { STRIPE_GATEWAY_ID, subscriptionTypes } from '@constants/index';
import {
  getPurchaseSubscription,
  getPaymentStatusResponse
} from '@selectors/checkout';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import useSelectedGoal, {
  useSelectedGoalUID
} from '@api/hooks/topology/useSelectedGoal';
import { LatestActiveUserSubscription } from '@selectors/subscription';
import handleAppPaymentRedirection from '@utils/handleAppPaymentRedirection';
import getPreservedQueryParams from '@utils/getPreservedQueryParams';
import { useIsMobile } from '@stores/AppCommonData';
import {
  STOREBUNDLE_PROCESSOR,
  STORESUBSCRIPTION_PROCESSOR,
  STORE_PROCESSOR
} from './constants/index';
import PaymentProcessingState from './MwebCheckOutV2/PaymentProcessingState';

const ProgressWrapper = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  grid-column: 5/21;
  margin-top: 100px;
  margin-bottom: ${Spacings.SPACING_12B};
  padding: 40px 80px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: 1/9;
    padding: 16px;
  }
`;
const PaymentMweb = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;
const PaymentWeb = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  left: calc(100% - 50%);
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;
const Title = styled(H3)`
  margin-top: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    text-align: center;
  }
`;

const Info = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_9B};
  text-align: center;
  width: 450px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
  }
`;

const sendPaymentEvents = (
  user,
  subscription,
  orderId,
  type,
  partPaymentInfo,
  selectedGoalDetails = null
) => {
  const isCombo = !!subscription?.value?.is_bundle_topology;
  const goalUID = subscription?.value?.uid || '';
  const goalName = subscription?.value?.name || '';
  const amount =
    subscription?.tax_breakdown.find((item) => item.label === 'Total')
      ?.amount || 0;
  const isUserSubscribed =
    user.plusSubscriptionUids?.indexOf(goalUID) > -1 || false;
  const duration = subscription?.duration || 0;
  const subscriptionType = subscription?.type;

  const gId = isCombo ? selectedGoalDetails?.uid : goalUID;
  const gName = isCombo ? selectedGoalDetails?.name : goalName;

  if (type === 'success') {
    sendPaymentSuccessfulEvent(
      gId,
      gName,
      duration,
      user.uid || '',
      user.username || '',
      amount,
      orderId,
      isUserSubscribed,
      subscriptionType,
      partPaymentInfo,
      isCombo,
      goalUID,
      goalName
    );
  } else if (type === 'failure') {
    sendPaymentFailureEvent(
      gId,
      gName,
      duration,
      user.uid || '',
      user.username || '',
      amount,
      orderId,
      isUserSubscribed,
      subscriptionType,
      partPaymentInfo,
      isCombo,
      goalUID,
      goalName
    );
  } else if (type === 'pending') {
    sendPaymentPendingEvent(
      gId,
      gName,
      duration,
      user.uid || '',
      user.username || '',
      amount,
      orderId,
      isUserSubscribed,
      subscriptionType,
      isCombo,
      goalUID,
      goalName
    );
  }
};

const PaymentProcessing = () => {
  const router = useRouter();
  const { getState } = useStore();
  const dispatch = useDispatchToStore();
  const { selectedGoal: selectedGoalDetails } = useSelectedGoal();

  const [paymentProcessingCompleted, setPaymentProcessingCompleted] =
    useState(false);
  const [statusUnknown, setStatusUnknown] = useState(false);
  const statusCheckCount = useRef(0);
  const { order_id, session_id, merchant_payment_code, gUid } = router.query;
  const orderId = useRef(order_id);
  const sessionId = useRef(session_id);
  const merchantPaymentCode = useRef(merchant_payment_code);
  const fromGoalUid = useRef(gUid);

  const purchaseSubscriptionResponse = useSelector(getPurchaseSubscription);
  const paymentStatusResponse = useSelector(getPaymentStatusResponse);
  const me = useMyInfo(getMe);
  const meJwt = useMyInfo(getJWTToken);
  const isMobile = useIsMobile();
  const [mwebCheckoutV2, setMwebCheckoutV2] = useState(false);

  useEffect(() => {
    if (meJwt) {
      fetchABExperimentData('mweb_checkout_reskin', meJwt).then(
        (experimentVariation) => {
          setMwebCheckoutV2(
            experimentVariation?.variation === abVariations.TEST_1
          );
        }
      );
    }
  }, [meJwt]);

  const { refetchAccessControlData } = useAccessControl({
    goalUID: fromGoalUid
  });

  useEffect(() => {
    if (orderId.current || merchantPaymentCode.current) {
      const gateway = merchantPaymentCode.current ? 5 : 2;
      dispatch(purchaseSubscription(orderId.current, gateway));
    }
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      const thankyouQueryObj = {
        source: router?.query?.source as string
      };
      const thankyouQueryParams = router?.query?.source
        ? new URLSearchParams(thankyouQueryObj).toString()
        : null;
      const thankyouQueryParamsURL = thankyouQueryParams
        ? `?${thankyouQueryParams}`
        : '';
      if (
        paymentStatusResponse &&
        purchaseSubscriptionResponse &&
        !paymentProcessingCompleted
      ) {
        if (!purchaseSubscriptionResponse.is_pending) {
          // success - goal, bundle
          if (
            paymentStatusResponse.state === 2 &&
            (purchaseSubscriptionResponse?.user_subscription?.subscription ||
              ((purchaseSubscriptionResponse?.subscription?.processor_name ===
                STORE_PROCESSOR ||
                purchaseSubscriptionResponse?.subscription?.processor_name ===
                  STORESUBSCRIPTION_PROCESSOR ||
                purchaseSubscriptionResponse?.user_subscription?.subscription
                  ?.processor_name === STOREBUNDLE_PROCESSOR) &&
                !purchaseSubscriptionResponse?.is_partial))
          ) {
            // setPaymentProcessingCompleted(true);
            const goalUID =
              purchaseSubscriptionResponse?.user_subscription?.subscription
                ?.value?.uid;
            const partPaymentInfo: any = {};
            if (
              purchaseSubscriptionResponse.is_partial &&
              purchaseSubscriptionResponse.is_partial_last_part_payment
            ) {
              const partPaymentStatus: any = await dispatch(
                fetchPartPaymentStatus(goalUID)
              );
              await refetchAccessControlData();
              partPaymentInfo.isPartPartment = true;
              partPaymentInfo.optedPartPayments =
                partPaymentStatus?.result?.part_payments?.length;
              partPaymentInfo.currentPartPaymentIndex =
                partPaymentStatus?.result?.part_payments?.length;
              partPaymentInfo.isLastPartPayment = true;
            }
            sendPaymentEvents(
              me,
              purchaseSubscriptionResponse?.user_subscription?.subscription,
              orderId.current,
              'success',
              partPaymentInfo,
              selectedGoalDetails
            );

            const isCombo =
              !!purchaseSubscriptionResponse.bundle_user_subscriptions?.length;

            // UAIconsGoal Subscription Confirmation
            if (goalUID === ICONS_GOAL_UID) {
              await UAIconsAPI.confirmSubscription(getState, dispatch);
            }

            // save previous subscriptions to figure out whether the user is a first time subscriber
            dispatch(setPrePaymentSubscriptionUids(me.plusSubscriptionUids));

            dispatch(
              savePurchasedSubscription(
                goalUID,
                orderId.current,
                isCombo
                  ? purchaseSubscriptionResponse.bundle_user_subscriptions
                  : purchaseSubscriptionResponse?.user_subscription
                      ?.subscription
              )
            );

            const isPlatform =
              purchaseSubscriptionResponse?.user_subscription?.subscription
                ?.type === subscriptionTypes.PLATFORM;
            const isStore =
              purchaseSubscriptionResponse?.subscription?.processor_name ===
              STORE_PROCESSOR;
            const isStoreSubscriptionBased =
              purchaseSubscriptionResponse?.user_subscription?.subscription
                ?.processor_name === STORESUBSCRIPTION_PROCESSOR;
            const isStoreBundle =
              purchaseSubscriptionResponse?.user_subscription?.subscription
                ?.processor_name === STOREBUNDLE_PROCESSOR;
            if (sessionId.current) {
              pushRoute(
                `/stripe/payment-status?status=success&goalUID=${goalUID}`
              );
            } else if (isPlatform) {
              const { user_subscription, order_id } =
                purchaseSubscriptionResponse;
              const queryObj = {
                orderId: order_id,
                contentTypeId: user_subscription?.subscription?.content_type_id,
                objectId: user_subscription?.subscription?.value?.id,
                source: router?.query?.source as string
              };
              const queryParams = new URLSearchParams(queryObj).toString();
              pushRoute(
                `/batch-group/[slug]/subscribe/thank-you?${queryParams}`,
                `/batch-group/${purchaseSubscriptionResponse?.user_subscription?.subscription?.value?.id}/subscribe/thank-you?${queryParams}`
              );
            } else if (isStore || isStoreSubscriptionBased || isStoreBundle) {
              const isRedirected = handleAppPaymentRedirection(
                router.query,
                'thankyou',
                null,
                '-',
                orderId.current
              );
              if (isRedirected) return;
              pushRoute(
                `/goal/[goalSlug]/[goalUID]/subscribe/thank-you/${thankyouQueryParamsURL}`,
                `/goal/-/-/subscribe/thank-you/${thankyouQueryParamsURL}`
              );
            } else {
              const isRedirected = handleAppPaymentRedirection(
                router.query,
                'thankyou',
                purchaseSubscriptionResponse.user_subscription.subscription
                  .value.relative_link,
                goalUID,
                orderId.current
              );
              if (isRedirected) return;
              const isPaidTrial =
                purchaseSubscriptionResponse?.user_subscription?.subscription
                  ?.type === 2 &&
                purchaseSubscriptionResponse?.user_subscription?.subscription
                  ?.sub_type === 2;
              const isPrimeSubscription =
                purchaseSubscriptionResponse?.user_subscription?.subscription
                  ?.content_type_id === 507;

              const isFixedSourceMWeb = isPrimeSubscription || isPaidTrial;
              const finalThankyouQueryParamsURL = isFixedSourceMWeb
                ? '?source=app'
                : thankyouQueryParamsURL;
              pushRoute(
                `/goal/[goalSlug]/[goalUID]/subscribe/thank-you/${finalThankyouQueryParamsURL}`,
                `${purchaseSubscriptionResponse.user_subscription.subscription.value.relative_link}/subscribe/thank-you/${finalThankyouQueryParamsURL}`
              );
            }
          } else if (
            // failure - goal, bundle
            paymentStatusResponse.state === 1 &&
            purchaseSubscriptionResponse.subscription
          ) {
            const fromGUid = fromGoalUid.current;
            const partPaymentInfo: any = {};
            const isPlatform =
              purchaseSubscriptionResponse.subscription.type ===
              subscriptionTypes.PLATFORM;
            const isStore =
              purchaseSubscriptionResponse?.subscription?.processor_name ===
              STORE_PROCESSOR;
            const subscriptionUID =
              purchaseSubscriptionResponse?.subscription?.uid;
            const isStoreSubscriptionBased =
              purchaseSubscriptionResponse?.subscription?.processor_name ===
              STORESUBSCRIPTION_PROCESSOR;
            const isStoreBundle =
              purchaseSubscriptionResponse?.subscription?.processor_name ===
              STOREBUNDLE_PROCESSOR;
            const extraParams = getQueryString({
              credsApplied: purchaseSubscriptionResponse.are_credits_applied,
              referralCode: purchaseSubscriptionResponse.referral?.code,
              gUid: fromGUid,
              payment_status: 'failed'
            });

            const storeGoalUID =
              purchaseSubscriptionResponse?.subscription?.value?.goal_uid;

            if (purchaseSubscriptionResponse.is_partial) {
              partPaymentInfo.isPartPartment = true;
              partPaymentInfo.isLastPartPayment =
                purchaseSubscriptionResponse.is_partial_last_part_payment ||
                false;
            }

            // Handling when payment status is coming failed from server.
            // setPaymentProcessingCompleted(true);
            sendPaymentEvents(
              me,
              purchaseSubscriptionResponse.subscription,
              orderId.current,
              'failure',
              partPaymentInfo,
              selectedGoalDetails
            );

            if (sessionId.current) {
              pushRoute(
                `/stripe/payment-status?status=failure&goalUID=${selectedGoalDetails.uid}`
              );
            } else if (isPlatform) {
              pushRoute(
                `/batch-group/[slug]/subscribe/[subscriptionUID]${extraParams}`,
                `/batch-group/${purchaseSubscriptionResponse.subscription.value.id}/subscribe/${purchaseSubscriptionResponse.subscription.uid}${extraParams}`
              );
            } else if (isStore || isStoreSubscriptionBased) {
              pushRoute(
                `/goal/[slug]/[goalUID]/subscribe/[subscriptionUID]${extraParams}`,
                `/goal/-/${storeGoalUID}/subscribe/${subscriptionUID}${extraParams}`
              );
            } else if (isStoreBundle) {
              const {
                goal_details: { name = '', uid = '' },
                subscription: { uid: subscriptionUID = '' }
              } = purchaseSubscriptionResponse ?? {};
              pushRoute(
                `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${extraParams}`,
                `/goal/${name}/${uid}/subscribe/${subscriptionUID}${extraParams}`
              );
            } else {
              pushRoute(
                `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${extraParams}`,
                `${purchaseSubscriptionResponse.subscription.value.relative_link}/subscribe/${purchaseSubscriptionResponse.subscription.uid}${extraParams}`
              );
            }
          } else if (
            // success - goal, bundle (part payment)
            paymentStatusResponse.state === 2 &&
            purchaseSubscriptionResponse.is_partial &&
            !purchaseSubscriptionResponse.is_partial_last_part_payment
          ) {
            const goalUID =
              purchaseSubscriptionResponse?.subscription?.value.uid;
            const partPaymentStatus: any = await dispatch(
              fetchPartPaymentStatus(goalUID)
            );
            await refetchAccessControlData();
            const partPaymentInfo = {
              isPartPartment: true,
              optedPartPayments:
                partPaymentStatus?.result?.part_payments?.length,
              currentPartPaymentIndex:
                partPaymentStatus?.result?.part_payments?.findIndex(
                  (item) => !item.payment
                ),
              isLastPartPayment: false
            };
            sendPaymentEvents(
              me,
              purchaseSubscriptionResponse.subscription,
              orderId.current,
              'success',
              partPaymentInfo,
              selectedGoalDetails
            );
            // setPaymentProcessingCompleted(true);
            pushRoute(
              `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${getPreservedQueryParams(
                { part_payment_status: 'success' }
              )}`,
              `${
                purchaseSubscriptionResponse?.subscription?.value
                  ?.relative_link ??
                `/goal/-/${purchaseSubscriptionResponse?.subscription?.value?.goal_uid}`
              }/subscribe/${
                purchaseSubscriptionResponse.subscription.uid
              }${getPreservedQueryParams({ part_payment_status: 'success' })}`
            );
          }
        }
      }
    })();
  }, [
    dispatch,
    paymentStatusResponse,
    purchaseSubscriptionResponse,
    paymentProcessingCompleted,
    me
  ]);

  const checkPaymentStatus = useCallback(() => {
    if (!paymentProcessingCompleted && statusCheckCount.current < 8) {
      statusCheckCount.current += 1;
      let gateway = 2;
      let gatewayName = 'juspay';
      if (sessionId.current) {
        gateway = STRIPE_GATEWAY_ID;
        gatewayName = 'stripe';
      }
      if (merchantPaymentCode.current) {
        gateway = 5;
        gatewayName = 'ebanx';
      }
      if (purchaseSubscriptionResponse?.state !== 2) {
        dispatch(
          purchaseSubscription(sessionId.current || orderId.current, gateway)
        );
      }
      dispatch(
        checkForPaymentStatusJuspay(
          orderId.current || sessionId.current,
          gatewayName,
          !!sessionId.current
        )
      );
    } else {
      // Handling when not getting final confirmation from server.
      // setPaymentProcessingCompleted(true);
      setStatusUnknown(true);
      sendPaymentEvents(
        me,
        purchaseSubscriptionResponse?.subscription,
        orderId.current,
        'pending',
        selectedGoalDetails
      );
    }
  }, [dispatch, purchaseSubscriptionResponse, paymentProcessingCompleted, me]);

  useInterval(checkPaymentStatus, !paymentProcessingCompleted ? 3000 : null);
  const subsGoalSelected = useSelectedGoalUID();
  const subsgoalUID =
    LatestActiveUserSubscription()?.subscription?.value?.uid ||
    subsGoalSelected;
  const backToPlus = () => {
    if (
      purchaseSubscriptionResponse?.subscription?.type ===
      subscriptionTypes.PLATFORM
    ) {
      pushRoute(
        '/batch-group/[slug]/[batchGroupUID]',
        `/batch-group/batch-slug/${purchaseSubscriptionResponse?.subscription?.uid}`
      );
    } else if (
      purchaseSubscriptionResponse?.subscription?.processor_name ===
        STORE_PROCESSOR ||
      purchaseSubscriptionResponse?.subscription?.processor_name ===
        STORESUBSCRIPTION_PROCESSOR
    ) {
      const isRedirected = handleAppPaymentRedirection(
        router.query,
        'home',
        `/goal/-/${subsgoalUID || '-'}`,
        subsgoalUID || ''
      );
      if (isRedirected) return;
      if (typeof subsgoalUID !== 'undefined') {
        pushRoute('/goal/[goalSlug]/[goalUID]/', `/goal/-/${subsgoalUID}`);
      } else {
        pushRoute('/explore', '/explore');
      }
    } else if (
      purchaseSubscriptionResponse?.subscription &&
      !purchaseSubscriptionResponse?.subscription?.value?.is_bundle_topology
    ) {
      cookies.setCookie(
        'selectedGoalUid',
        purchaseSubscriptionResponse.subscription.value.uid
      );
      const isRedirected = handleAppPaymentRedirection(
        router.query,
        'home',
        null,
        purchaseSubscriptionResponse.subscription.value.uid
      );
      if (isRedirected) return;
      pushRoute(
        '/goal/[goalSlug]/[goalUID]',
        purchaseSubscriptionResponse.subscription.value.relative_link
      );
    } else {
      pushRoute('/', '/');
    }
  };

  /* This experimentalComponenet was used after line 593 in this page
  // isExperimentEnabled ? (

  // ) :
  */
  return (
    <>
      <PaymentWeb>
        <ProgressWrapper>
          <ClockLoader />
          <Title>
            {statusUnknown
              ? 'It’s taking longer than usual'
              : 'Your payment is in progress'}
          </Title>
          <Info>
            {statusUnknown
              ? 'We will notify you once we get a confirmation from the payment provider. Kindly wait for sometime.'
              : 'Please do not refresh the page and wait while we are processing your payment. This can take a few minutes.'}
          </Info>
          {statusUnknown && (
            <Button
              // @ts-ignore
              label="Back to home"
              onClick={backToPlus}
            />
          )}
        </ProgressWrapper>
      </PaymentWeb>
      <PaymentMweb>
        <PaymentProcessingState
          title={
            statusUnknown
              ? 'It’s taking longer than usual'
              : 'Your payment is in progress'
          }
          subTitle="Please wait a moment"
          icon={
            statusUnknown
              ? 'payments/mweb-checkout-v2/thank-you/taking-longer-than-usual.svg'
              : 'payments/mweb-checkout-v2/thank-you/payment-in-progress.svg'
          }
          iconAlt={
            statusUnknown ? 'taking longer than usual' : 'payment in progress'
          }
          ctaText={statusUnknown ? 'Okey' : null}
          handleCta={statusUnknown ? backToPlus : () => {}}
        />
      </PaymentMweb>
    </>
  );
};

export default PaymentProcessing;
